import React from 'react'
import {ArrowLeft, DollarSign, Upload, Eye, TrendingUp, CheckCircle, Shield} from 'lucide-react'

interface HowItWorksPageProps {
  onBack: () => void
}

export function HowItWorksPage({ onBack }: HowItWorksPageProps) {
  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0A0E27] via-[#0d1339] to-[#0A0E27] pb-16">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Back Button */}
        <button
          onClick={onBack}
          className="flex items-center gap-2 px-4 py-2 bg-white/5 text-white rounded-lg hover:bg-white/10 transition-colors mb-8"
        >
          <ArrowLeft className="w-5 h-5" />
          Back to Home
        </button>

        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="text-5xl font-bold text-white mb-6">
            How Base Builders Works
          </h1>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            A simple, transparent platform connecting NFT & crypto projects with thousands of potential investors and users
          </p>
        </div>

        {/* For Project Owners */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-white mb-8 flex items-center gap-3">
            <Upload className="w-8 h-8 text-[#60A5FA]" />
            For Project Owners
          </h2>
          
          <div className="space-y-6">
            <div className="bg-white/5 border border-white/10 rounded-xl p-6">
              <div className="flex items-start gap-4">
                <div className="flex items-center justify-center w-12 h-12 bg-gradient-to-r from-[#0052FF] to-[#60A5FA] rounded-lg text-white font-bold text-xl flex-shrink-0">
                  1
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white mb-2">Sign Up & Subscribe</h3>
                  <p className="text-gray-400">
                    Create an account and get a 1-month free trial. Payment info is required but you won't be charged until after 30 days. Then just $20/month with no hidden fees, cancel anytime.
                  </p>
                  <p className="text-sm text-cyan-400 mt-2">
                    💡 Cancel anytime during your free trial and you won't be charged. Your project will be delisted immediately upon cancellation.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white/5 border border-white/10 rounded-xl p-6">
              <div className="flex items-start gap-4">
                <div className="flex items-center justify-center w-12 h-12 bg-gradient-to-r from-[#0052FF] to-[#60A5FA] rounded-lg text-white font-bold text-xl flex-shrink-0">
                  2
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white mb-2">Create Your Profile</h3>
                  <p className="text-gray-400">
                    Upload a banner image, profile picture, description, and add links to all your social channels, website, and charts. Build a media gallery with images and videos.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white/5 border border-white/10 rounded-xl p-6">
              <div className="flex items-start gap-4">
                <div className="flex items-center justify-center w-12 h-12 bg-gradient-to-r from-[#0052FF] to-[#60A5FA] rounded-lg text-white font-bold text-xl flex-shrink-0">
                  3
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white mb-2">Go Live Instantly</h3>
                  <p className="text-gray-400">
                    Once payment is confirmed, your project automatically appears on the homepage and explore page. No manual approval needed.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white/5 border border-white/10 rounded-xl p-6">
              <div className="flex items-start gap-4">
                <div className="flex items-center justify-center w-12 h-12 bg-gradient-to-r from-[#0052FF] to-[#60A5FA] rounded-lg text-white font-bold text-xl flex-shrink-0">
                  4
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white mb-2">Track Your Performance</h3>
                  <p className="text-gray-400">
                    View real-time analytics including page views and clicks to your website. Update your profile anytime from your dashboard.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* For Investors & Users */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-white mb-8 flex items-center gap-3">
            <Eye className="w-8 h-8 text-[#60A5FA]" />
            For Investors & Users
          </h2>
          
          <div className="space-y-6">
            <div className="bg-white/5 border border-white/10 rounded-xl p-6">
              <div className="flex items-start gap-4">
                <CheckCircle className="w-8 h-8 text-green-400 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-xl font-bold text-white mb-2">Discover Quality Projects</h3>
                  <p className="text-gray-400">
                    Browse a curated list of active NFT and crypto projects on Base blockchain. All projects are paid listings, ensuring serious commitment from project teams.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white/5 border border-white/10 rounded-xl p-6">
              <div className="flex items-start gap-4">
                <CheckCircle className="w-8 h-8 text-green-400 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-xl font-bold text-white mb-2">Explore Detailed Profiles</h3>
                  <p className="text-gray-400">
                    Click on any project to view their full profile with banner, description, media gallery, and direct links to their website and social channels.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white/5 border border-white/10 rounded-xl p-6">
              <div className="flex items-start gap-4">
                <CheckCircle className="w-8 h-8 text-green-400 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-xl font-bold text-white mb-2">Connect Directly</h3>
                  <p className="text-gray-400">
                    Visit project websites, follow on social media, check charts, and join communities. All in one convenient hub.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Benefits */}
        <div className="bg-gradient-to-br from-[#0052FF]/20 to-[#60A5FA]/20 border border-[#0052FF]/30 rounded-2xl p-8 md:p-12 mb-16">
          <h2 className="text-3xl font-bold text-white mb-8 text-center">Why Base Builders?</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-r from-[#0052FF] to-[#60A5FA] rounded-full flex items-center justify-center">
                <DollarSign className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Affordable</h3>
              <p className="text-gray-400">
                1-month free trial, then just $20/month with no hidden fees or long-term contracts
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-r from-[#0052FF] to-[#60A5FA] rounded-full flex items-center justify-center">
                <TrendingUp className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">High Visibility</h3>
              <p className="text-gray-400">
                Get your project in front of thousands of crypto enthusiasts and investors
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-r from-[#0052FF] to-[#60A5FA] rounded-full flex items-center justify-center">
                <Shield className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Instant & Easy</h3>
              <p className="text-gray-400">
                Automated approval, easy setup, and real-time analytics dashboard
              </p>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="text-center">
          <h2 className="text-3xl font-bold text-white mb-4">Ready to Get Started?</h2>
          <p className="text-gray-400 mb-6">
            Join Base Builders today and showcase your project to the Base ecosystem
          </p>
          <button
            onClick={onBack}
            className="px-8 py-4 bg-gradient-to-r from-[#0052FF] to-[#60A5FA] text-white rounded-lg font-bold text-lg hover:opacity-90 transition-opacity shadow-lg shadow-[#0052FF]/50"
          >
            List Your Project
          </button>
        </div>
      </div>
    </div>
  )
}
